import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmptyState = ({ 
  type = 'no-exercises', // 'no-exercises' | 'no-results' | 'no-plan'
  searchQuery = '',
  onClearFilters,
  className = '' 
}) => {
  const getEmptyStateConfig = () => {
    switch (type) {
      case 'no-results':
        return {
          icon: 'Search',
          title: 'No exercises found',
          description: `No exercises match your current search "${searchQuery}" or filter criteria.`,
          actionText: 'Clear Filters',
          actionIcon: 'RotateCcw',
          onAction: onClearFilters,
          secondaryAction: null
        };
      
      case 'no-plan':
        return {
          icon: 'FileText',
          title: 'No career plan yet',
          description: 'Create your personalized career development plan to get started with weekly exercises and track your progress.',
          actionText: 'Create Career Plan',
          actionIcon: 'Plus',
          actionLink: '/ai-plan-generation',
          secondaryAction: {
            text: 'Take Assessment',
            icon: 'ClipboardList',
            link: '/career-assessment-setup'
          }
        };
      
      default: // 'no-exercises'
        return {
          icon: 'Target',
          title: 'No exercises this week',
          description: 'Generate your weekly check-in to receive personalized exercises based on your career development plan.',
          actionText: 'Generate Weekly Check-in',
          actionIcon: 'RefreshCw',
          onAction: () => {}, // This will be handled by parent component
          secondaryAction: {
            text: 'View Progress',
            icon: 'BarChart3',
            link: '/progress-dashboard'
          }
        };
    }
  };

  const config = getEmptyStateConfig();

  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`}>
      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
        <Icon name={config?.icon} size={32} className="text-muted-foreground" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">
        {config?.title}
      </h3>
      <p className="text-muted-foreground mb-6 max-w-md leading-relaxed">
        {config?.description}
      </p>
      <div className="space-y-3">
        {config?.actionLink ? (
          <Button
            variant="default"
            size="default"
            iconName={config?.actionIcon}
            iconPosition="left"
            asChild
          >
            <Link to={config?.actionLink}>{config?.actionText}</Link>
          </Button>
        ) : (
          <Button
            variant="default"
            size="default"
            iconName={config?.actionIcon}
            iconPosition="left"
            onClick={config?.onAction}
          >
            {config?.actionText}
          </Button>
        )}

        {config?.secondaryAction && (
          <Button
            variant="outline"
            size="default"
            iconName={config?.secondaryAction?.icon}
            iconPosition="left"
            asChild
          >
            <Link to={config?.secondaryAction?.link}>
              {config?.secondaryAction?.text}
            </Link>
          </Button>
        )}
      </div>
      {type === 'no-exercises' && (
        <div className="mt-8 p-4 bg-muted/50 rounded-lg max-w-md">
          <div className="flex items-start space-x-3">
            <Icon name="Lightbulb" size={16} className="text-primary mt-0.5" />
            <div className="text-left">
              <h4 className="text-sm font-medium text-foreground mb-1">
                How it works
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Weekly check-ins generate 2-4 personalized exercises based on your career goals, 
                current progress, and development areas identified in your assessment.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmptyState;